import refreshAccessToken from "./refreshAccessToken";
import { logError, logInfo } from "./shouldLog";

// No request may hang forever. This is the TRANSPORT ceiling, not a screen's
// own loading budget. It sits above the slowest real endpoint so it never
// cuts a working request short.
const REQUEST_TIMEOUT_MS = 60000;

const SDK_LABEL = "pool-api 0.0.1";

const SENSITIVE_HEADERS = [
  "x-access-token",
  "x-refresh-token",
  "authorization",
  "cookie",
  "set-cookie",
];

function redactHeaders(
  headers?: Record<string, string>,
): Record<string, string> | undefined {
  if (!headers) return undefined;
  const safe: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    safe[name] = SENSITIVE_HEADERS.includes(name.toLowerCase())
      ? `[redacted, ${value.length} chars]`
      : value;
  }
  return safe;
}

type Payload = {
  httpMethod: "POST";
  endpoint: string;
  payload: object;
  options: {
    headers?: Record<string, string>;
    apiHost?: string;
    prefix?: string;
    timeoutMs?: number;
  };
};

export default async function call(
  { httpMethod, endpoint, payload, options }: Payload,
  isRetry = false,
): Promise<{
  data: unknown;
  error: { code: string; message: string; details?: unknown } | null;
}> {
  const url = `${options.apiHost ?? ""}${endpoint}`;
  const abortController =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
  // Held on an object rather than in a plain `let`: this flag is only ever
  // set from inside the setTimeout callback below, and a plain local would be
  // narrowed to the literal false everywhere the callback has not visibly
  // run, which is every line that actually needs to read it.
  const timeoutState = { didTimeOut: false };
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  if (abortController) {
    timeoutHandle = setTimeout(() => {
      timeoutState.didTimeOut = true;
      abortController.abort();
    }, timeoutMs);
  }

  let result: {
    data: unknown;
    error: { code: string; message: string; details?: unknown } | null;
  };
  try {
    // Keys are spread in only when there is a real value: RequestInit's own
    // headers and signal properties are not undefined-able, so under
    // exactOptionalPropertyTypes an absent value has to be an ABSENT KEY, not
    // an explicit undefined.
    const response = await fetch(url, {
      method: httpMethod,
      mode: "cors",
      cache: "no-cache",
      body: JSON.stringify(payload),
      ...(options.headers ? { headers: options.headers } : {}),
      ...(abortController ? { signal: abortController.signal } : {}),
    });

    const isTokenExpiryToRetry =
      !isRetry &&
      response.status === 401 &&
      Boolean(options.headers?.["x-access-token"]);
    if (!response.ok) {
      const say = isTokenExpiryToRetry ? logInfo : logError;
      say(
        `Failed to call API. ${httpMethod} ${url} -> ${response.status} ${response.statusText}`,
        {
          sdk: SDK_LABEL,
          headers: redactHeaders(options.headers),
        },
      );
    }

    // The body read and parse get their own catch, nested inside this one.
    // `fetch` having resolved means a server answered and handed us a status,
    // so a body we cannot read or cannot parse is a server fault, never an
    // unreachable network. An abort that lands mid-body is rethrown, so a
    // timeout still reports as a timeout.
    try {
      const text = await response.text();
      // Empty 2xx is a successful empty payload. Any other body we cannot turn
      // into an envelope is a failure, and callers that check
      // `if (result.error)` must see an error envelope.
      if (text.trim()) {
        result = JSON.parse(text) as {
          data: unknown;
          error: { code: string; message: string; details?: unknown } | null;
        };
      } else if (response.ok) {
        result = { error: null, data: null };
      } else {
        result = {
          error: {
            code: "INTERNAL_ERROR",
            message: `HTTP ${response.status}`,
            details: { status: response.status },
          },
          data: null,
        };
      }
    } catch (bodyError: unknown) {
      if (timeoutState.didTimeOut) throw bodyError;
      const bodyErr = bodyError as { name?: string; message?: string };
      logError(
        `Failed to read API response. ${httpMethod} ${url} -> ${response.status}: ${bodyErr.name}: ${bodyErr.message}`,
        { sdk: SDK_LABEL },
      );
      result = {
        error: {
          code: "INTERNAL_ERROR",
          message: `HTTP ${response.status}`,
          details: { status: response.status },
        },
        data: null,
      };
    }
  } catch (e: unknown) {
    const err = e as { name?: string; message?: string };
    logError(
      `Failed to call API. ${httpMethod} ${url}: ${err.name}: ${err.message}${timeoutState.didTimeOut ? ` (timed out after ${timeoutMs}ms)` : ""}`,
      { sdk: SDK_LABEL },
    );
    // The request never reached a server, so this is not a server fault. A
    // timeout lands here too and deliberately carries the same code, because no
    // answer arrived either way and the next step is identical. Every response
    // that carried a status is resolved by the nested try above and keeps
    // INTERNAL_ERROR: a status number means a server answered.
    result = {
      error: {
        code: "NETWORK_UNREACHABLE",
        message: timeoutState.didTimeOut
          ? `Request timed out after ${timeoutMs}ms: ${url}`
          : (err.message ?? "Network error"),
      },
      data: null,
    };
  } finally {
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
  }

  // Reactive refresh: the access token expired mid-request. Refresh once and
  // retry with the new token, single-flight via refreshAccessToken.
  if (
    !isRetry &&
    result.error?.code === "UNAUTHENTICATED" &&
    options.headers?.["x-access-token"]
  ) {
    const fresh = await refreshAccessToken({ timeoutMs });
    if (fresh) {
      return call(
        {
          httpMethod,
          endpoint,
          payload,
          options: {
            ...options,
            headers: { ...options.headers, "x-access-token": fresh },
          },
        },
        true,
      );
    }
  }

  return result;
}
